// Starter Tape hub — /starter-tape. Side A's tapes (done / open / locked),
// then each path's tapes (Deep Space first; paths open after Side A), the
// sandbox, and the Tape Shelf of everything you've saved, each take
// playable in place.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "../lib/utils";
import {
  COMING_SOON,
  PATHS,
  SIDE_A,
  getTape,
} from "../lib/starterTapeCurriculum";
import {
  completedCount,
  isSandboxUnlocked,
  isUnlocked,
  removeTake,
} from "../lib/starterTapeProgress";

import TapeShell, { THEMES } from "../components/starterTape/TapeShell";
import useTapePlayer from "../components/starterTape/useTapePlayer";
import { loadProgress, saveProgress } from "../components/starterTape/storage";

const formatDate = (ms) =>
  new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

// One tape on the hub: number, title, idea, and done / open / locked.
// Literal accent classes so Tailwind's JIT scanner picks them up.
const ACCENTS = {
  amber: {
    open: "bg-amber-300/15 text-amber-300",
    hover: "hover:border-amber-300/60",
  },
  indigo: {
    open: "bg-indigo-300/15 text-indigo-300",
    hover: "hover:border-indigo-300/60",
  },
};

function TapeCard({ tape, progress, accent = "amber" }) {
  const open = isUnlocked(progress, tape.id);
  const complete = !!progress.completed[tape.id];
  const a = ACCENTS[accent];
  const body = (
    <>
      <span className="text-xs text-zinc-500">Tape {tape.number}</span>
      <span className="mt-1 block text-lg font-semibold text-zinc-100">
        {tape.title}
      </span>
      <span className="mt-1 block text-sm text-zinc-400">
        {tape.idea.title}
      </span>
      <span
        className={cn(
          "mt-3 inline-block rounded-full px-2 py-0.5 text-xs",
          complete
            ? "bg-emerald-400/15 text-emerald-300"
            : open
              ? a.open
              : "bg-zinc-800 text-zinc-500"
        )}
      >
        {complete ? "✓ done" : open ? "open" : "locked"}
      </span>
    </>
  );
  return (
    <li>
      {open ? (
        <Link
          to={`/starter-tape/tape/${tape.id}`}
          className={cn(
            "block h-full rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 transition-colors",
            a.hover
          )}
        >
          {body}
        </Link>
      ) : (
        <div className="h-full rounded-xl border border-zinc-800/60 bg-zinc-900/30 p-4 opacity-60">
          {body}
        </div>
      )}
    </li>
  );
}

export default function StarterTapeHub() {
  const [progress, setProgress] = useState(loadProgress);
  const [playingId, setPlayingId] = useState(null);
  const playingTake = progress.shelf.find((t) => t.id === playingId) || null;
  const player = useTapePlayer(playingTake ? playingTake.song : null);

  useEffect(() => saveProgress(progress), [progress]);

  const done = completedCount(progress);
  const sandboxOpen = isSandboxUnlocked(progress);

  const toggleTake = (take) => {
    if (playingId === take.id && player.playing) {
      player.stop();
      setPlayingId(null);
    } else {
      setPlayingId(take.id);
      player.play(take.song);
    }
  };

  const deleteTake = (take) => {
    if (!window.confirm(`Remove “${take.title}” from your shelf?`)) return;
    if (playingId === take.id) {
      player.stop();
      setPlayingId(null);
    }
    setProgress(removeTake(progress, take.id));
  };

  return (
    <TapeShell
      title="Starter Tape"
      subtitle="Learn how music is put together by making it. Every tape is a short track of your own, and each one unlocks a little more studio."
    >
      {/* Side A */}
      <section className="mb-10">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-xl font-semibold">Side A: First Sounds</h2>
          <span className="text-sm text-zinc-500">
            {done} of {SIDE_A.length + COMING_SOON.length} done
          </span>
        </div>
        <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {SIDE_A.map((tape) => (
            <TapeCard key={tape.id} tape={tape} progress={progress} />
          ))}
          {COMING_SOON.map((tape) => (
            <li
              key={tape.number}
              className="rounded-xl border border-dashed border-zinc-800 p-4 opacity-50"
            >
              <span className="text-xs text-zinc-500">Tape {tape.number}</span>
              <span className="mt-1 block text-lg font-semibold text-zinc-300">
                {tape.title}
              </span>
              <span className="mt-1 block text-sm text-zinc-500">
                {tape.idea}
              </span>
              <span className="mt-3 inline-block text-xs text-zinc-500">
                coming soon
              </span>
            </li>
          ))}
        </ol>
      </section>

      {/* Paths */}
      {PATHS.map((path) => {
        const pathOpen = isUnlocked(progress, path.tapes[0].id);
        return (
          <section
            key={path.id}
            className="mb-10 rounded-2xl border border-indigo-400/20 p-4 sm:p-5"
            style={{
              backgroundImage: THEMES[path.theme].style.backgroundImage,
            }}
          >
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <p className="text-xs uppercase tracking-widest text-indigo-300/80">
                  Path
                </p>
                <h2 className="text-xl font-semibold">{path.name}</h2>
                <p className="mt-1 max-w-xl text-sm text-zinc-400">
                  {path.blurb}
                </p>
              </div>
              <span className="text-sm text-zinc-500">
                {pathOpen
                  ? `${completedCount(progress, path.tapes)} of ${path.tapes.length} done`
                  : "opens after Side A"}
              </span>
            </div>
            <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {path.tapes.map((tape) => (
                <TapeCard
                  key={tape.id}
                  tape={tape}
                  progress={progress}
                  accent="indigo"
                />
              ))}
            </ol>
            {path.id === "deepSpace" &&
              (isSandboxUnlocked(progress, "nebula") ? (
                <Link
                  to="/starter-tape/sandbox/deep-space"
                  className="mt-3 block rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 hover:border-indigo-300/60"
                >
                  <span className="font-semibold text-zinc-100">
                    Deep Space sandbox
                  </span>
                  <span className="ml-2 text-sm text-zinc-400">
                    Every Deep Space tool you've unlocked, no brief.
                  </span>
                </Link>
              ) : (
                <div className="mt-3 rounded-xl border border-zinc-800/60 p-4 text-sm text-zinc-500">
                  <span className="font-semibold text-zinc-400">
                    Deep Space sandbox
                  </span>{" "}
                  · opens after Nebula
                </div>
              ))}
          </section>
        );
      })}

      {/* Sandbox */}
      <section className="mb-10">
        {sandboxOpen ? (
          <Link
            to="/starter-tape/sandbox"
            className="block rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 hover:border-amber-300/60"
          >
            <span className="font-semibold text-zinc-100">Sandbox</span>
            <span className="ml-2 text-sm text-zinc-400">
              A free studio with everything you've unlocked.
            </span>
          </Link>
        ) : (
          <div className="rounded-xl border border-zinc-800/60 p-4 text-sm text-zinc-500">
            <span className="font-semibold text-zinc-400">Sandbox</span> · opens
            after tape 3
          </div>
        )}
      </section>

      {/* Tape Shelf */}
      <section>
        <h2 className="mb-3 text-xl font-semibold">Tape Shelf</h2>
        {progress.shelf.length === 0 ? (
          <p className="text-zinc-500">
            Nothing here yet. Finish a tape and save it, and it lands on your
            shelf.
          </p>
        ) : (
          <ul className="space-y-2">
            {progress.shelf.map((take) => {
              const tape = take.tapeId ? getTape(take.tapeId) : null;
              const isPlaying = playingId === take.id && player.playing;
              return (
                <li
                  key={take.id}
                  className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-3"
                >
                  <button
                    type="button"
                    onClick={() => toggleTake(take)}
                    aria-label={`${isPlaying ? "Stop" : "Play"} ${take.title}`}
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                      isPlaying
                        ? "bg-zinc-100 text-zinc-900"
                        : "bg-amber-300 text-zinc-900 hover:bg-amber-200"
                    )}
                  >
                    {isPlaying ? "■" : "▶"}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-zinc-100">
                      {take.title}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {tape ? `Tape ${tape.number}` : "Sandbox"} ·{" "}
                      {formatDate(take.savedAt)}
                      {tape && !take.met && " · must not met yet"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => deleteTake(take)}
                    className="rounded-lg px-2 py-1 text-xs text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300"
                  >
                    remove
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </TapeShell>
  );
}
